import { ThemeIcon } from "@mantine/core";
import IconCaretDownFilled from "@src/assets/icons/tabler/caret-down.svg?react";
import IconCaretRightFilled from "@src/assets/icons/tabler/caret-right.svg?react";
import IconStar from "@src/assets/icons/tabler/star.svg?react";
import { fetchNavbarBranchQueryOptions } from "@src/components/board/queries/fetch-ranklist";
import { NavbarSection } from "@src/gql/graphql";
import type { BranchData } from "@src/lib/clean-tree";
import { Tree } from "@src/lib/clean-tree";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useCallback, useMemo, useState } from "react";
import { useNavPendingRename } from "../context/nav-pending-rename-context";
import { useSearch } from "../context/search-context";
import { NavSectionLoadingItem } from "../nav-section-panel/nav-section-loading-item";
import NavSectionTextLabel from "../nav-section-panel/nav-section-text-label";
import { createNavTreeItemRenderer } from "./nav-tree-item";
import { TREE_INDENT_PER_LEVEL_PX, readTreeOpenState } from "./tree-indent";
import { useNavBranchItems, useRanklistTree } from "./use-ranklist-tree";

const OPEN_STATE_KEY = "nav-favourites-tree-open-state";

const FAVOURITES_SECTION = NavbarSection.Favourites;

function useFavouritesBranchItems(branchId: string | null): BranchData {
	return useNavBranchItems(branchId, FAVOURITES_SECTION);
}

export function FavouritesPanel({
	hideSectionHeader = false,
}: {
	hideSectionHeader?: boolean;
} = {}) {
	const { handleMoveItem, handleDeleteFolder } =
		useRanklistTree(FAVOURITES_SECTION);
	const { pendingRenameItemId, consumeRename } = useNavPendingRename();
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [initialOpenState] = useState(() => readTreeOpenState(OPEN_STATE_KEY));

	const { items: rootItems, isLoading } = useFavouritesBranchItems(null);
	const { query: navSearchQuery } = useSearch();
	const treeDragAndDropEnabled = !navSearchQuery.trim();

	const { data: favouriteBranch } = useQuery(
		fetchNavbarBranchQueryOptions(FAVOURITES_SECTION, null),
	);
	const favouriteKeys = useMemo(
		() => new Set((favouriteBranch ?? []).map((item) => item.key)),
		[favouriteBranch],
	);

	const handleDeleteFolderFromMenu = useCallback(
		(folderId: string) => {
			handleDeleteFolder(folderId, null);
		},
		[handleDeleteFolder],
	);

	const noopMoveToFolder = useCallback(() => {}, []);

	const renderNavTreeItem = useMemo(
		() =>
			createNavTreeItemRenderer({
				favouriteKeys,
				onDeleteFolder: handleDeleteFolderFromMenu,
				onMoveToFolder: noopMoveToFolder,
				activeSource: "favourites",
				navbarSection: FAVOURITES_SECTION,
				showMoveToFolder: false,
				boardRowAction: "unfavourite-only",
				pendingRenameItemId,
				onPendingRenameHandled: consumeRename,
			}),
		[
			favouriteKeys,
			handleDeleteFolderFromMenu,
			noopMoveToFolder,
			pendingRenameItemId,
			consumeRename,
		],
	);

	if (isLoading) {
		return <NavSectionLoadingItem />;
	}

	if (rootItems.length === 0) {
		if (hideSectionHeader) {
			return (
				<div className="flex flex-col items-center justify-center py-10 text-center text-text-muted px-4">
					<IconStar className="w-8 h-8 mb-2 opacity-30" />
					<p className="text-xs font-medium">No favorites yet</p>
					<p className="text-[11px] text-text-secondary mt-1">
						Star boards, folders, or cards to quickly access them here.
					</p>
				</div>
			);
		}
		return null;
	}

	return (
		<div className={clsx("rounded-lg w-0 min-w-full pr-1 pl-0")}>
			{!hideSectionHeader && (
				<div
					className={clsx(
						"inline-flex items-center justify-center max-w-full",
						"min-w-full cursor-pointer whitespace-nowrap gap-0.5 h-8 bg-surface-default",
						"sticky top-0 z-10",
					)}
					onClick={() => setIsCollapsed((v) => !v)}
				>
					<ThemeIcon
						variant="transparent"
						size="xs"
						color="var(--ui-color-text-primary)"
					>
						{!isCollapsed ? <IconCaretDownFilled /> : <IconCaretRightFilled />}
					</ThemeIcon>

					<NavSectionTextLabel
						className="grow pointer-events-auto select-none max-w-full ml-2 font-bold text-sm sm:text-xs text-text-secondary uppercase"
						label="Favorites"
						size="sm"
					/>
				</div>
			)}

			{(hideSectionHeader || !isCollapsed) && rootItems.length > 0 && (
				<Tree
					useBranchItems={useFavouritesBranchItems}
					renderItem={renderNavTreeItem}
					indentPerLevel={TREE_INDENT_PER_LEVEL_PX}
					dragAndDropEnabled={treeDragAndDropEnabled}
					initialOpenState={initialOpenState}
					onOpenStateChange={(id, isOpen) => {
						const state = readTreeOpenState(OPEN_STATE_KEY);
						state[id] = isOpen;
						localStorage.setItem(OPEN_STATE_KEY, JSON.stringify(state));
					}}
					onMoveItem={handleMoveItem}
					onDeleteFolder={handleDeleteFolder}
					renderLoading={() => <NavSectionLoadingItem />}
				/>
			)}
		</div>
	);
}
